/**
 * The thread in the order things happened (docs/plans/os-voice-conversation.md,
 * C10, J10; the 7 Oct fix list, item 1), pure, for the server and both
 * clients alike.
 *
 * On a capable call the server stamps every line and message it keeps or
 * sends with `at`: the server's time, in milliseconds, when it happened — an
 * OS line when its first audio went out, the person's line when its last
 * words were heard, Coach's answer when it landed, the divider when live mode
 * ended. A line frame carries it as `at`, a message as `metadata.at`.
 *
 * Writes do not happen in that order: a line is kept when it closes, the
 * person's line when it settles, an answer when its turn ends, each on its
 * own chain. So every reader orders by the stamp (`inSpokenOrder`) and never
 * by when a write landed. A message with no stamp (a typed turn, a thread
 * from before stamps) keeps its place after everything before it: its key is
 * the newest stamp seen so far, so it never jumps.
 */

/** The order stamp a message's metadata carries, or null when it has none. */
export const orderStampOf = (metadata: unknown): number | null => {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const at = (metadata as { at?: unknown }).at;
  return typeof at === 'number' && Number.isFinite(at) ? at : null;
};

/** A message's metadata with its order stamp put in; a stamp already there is kept. */
export const withOrderStamp = <M extends { readonly metadata?: unknown }>(message: M, at: number): M => {
  if (orderStampOf(message.metadata) !== null) return message;
  const metadata = typeof message.metadata === 'object' && message.metadata !== null ? message.metadata : {};
  return { ...message, metadata: { ...metadata, at } };
};

/**
 * The items in the order they happened: by stamp, a stable sort. An item
 * with none sorts at the newest stamp before it (so it stays after what it
 * followed), and ties keep the order given.
 */
export const inSpokenOrder = <T>(items: readonly T[], stampOf: (item: T) => number | null): T[] => {
  let newest = Number.NEGATIVE_INFINITY;
  const keyed = items.map((item, index) => {
    const stamp = stampOf(item);
    if (stamp !== null && stamp > newest) newest = stamp;
    return { item, index, key: stamp ?? newest };
  });
  keyed.sort((a, b) => (a.key === b.key ? a.index - b.index : a.key < b.key ? -1 : 1));
  return keyed.map((one) => one.item);
};

/** Messages in the order they happened, by `metadata.at`. */
export const messagesInSpokenOrder = <M extends { readonly metadata?: unknown }>(messages: readonly M[]): M[] =>
  inSpokenOrder(messages, (message) => orderStampOf(message.metadata));
